import React, { useState } from "react";
import axios from "axios";
import { Sparkles, Copy, Check, FileText, MessageSquare, Mail, Award, Megaphone, Loader2 } from "lucide-react";
import { API } from "./shared";

const CONTENT_TYPES = [
  { id: "blog_post", label: "Blog Post", icon: FileText, desc: "SEO-friendly article" },
  { id: "program_description", label: "Program Description", icon: Award, desc: "Compelling program copy" },
  { id: "social_media", label: "Social Media", icon: Megaphone, desc: "Engaging social post" },
  { id: "email_newsletter", label: "Email Newsletter", icon: Mail, desc: "Subscriber newsletter" },
  { id: "success_story", label: "Success Story", icon: MessageSquare, desc: "Client journey story" },
];

const TONES = [
  { id: "professional", label: "Professional" },
  { id: "warm", label: "Warm & Empathetic" },
  { id: "conversational", label: "Conversational" },
  { id: "inspiring", label: "Inspiring" },
];

export default function AIContentTab({ token }) {
  const [contentType, setContentType] = useState("blog_post");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("professional");
  const [additionalContext, setAdditionalContext] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    setGenerating(true);
    setError("");
    setResult("");
    try {
      const res = await axios.post(
        `${API}/ai-content/generate`,
        { content_type: contentType, topic: topic.trim(), tone, additional_context: additionalContext.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data.content);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to generate content. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6" data-testid="ai-content-tab">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
          <Sparkles size={20} className="text-accent" />
        </div>
        <div>
          <h2 className="font-serif text-xl text-emerald_deep">AI Content Generator</h2>
          <p className="text-xs text-text_muted">Generate marketing content powered by Claude AI</p>
        </div>
      </div>

      {/* Content Type Selector */}
      <div>
        <label className="block text-xs text-text_muted mb-2 tracking-wider uppercase">Content Type</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {CONTENT_TYPES.map((ct) => (
            <button key={ct.id} onClick={() => setContentType(ct.id)}
              data-testid={`content-type-${ct.id}`}
              className={`p-3 rounded-xl border text-left transition-all ${
                contentType === ct.id
                  ? "bg-emerald_deep text-bone border-emerald_deep"
                  : "bg-white text-text_main border-emerald_deep/10 hover:border-emerald_deep/30"
              }`}>
              <ct.icon size={16} className={contentType === ct.id ? "text-accent" : "text-text_muted"} />
              <p className="text-xs font-medium mt-1.5">{ct.label}</p>
              <p className={`text-[10px] mt-0.5 ${contentType === ct.id ? "text-bone/60" : "text-text_muted/60"}`}>{ct.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Topic Input */}
      <div>
        <label className="block text-xs text-text_muted mb-2 tracking-wider uppercase">Topic *</label>
        <input type="text" value={topic} onChange={(e) => setTopic(e.target.value)}
          placeholder="e.g., PCOS diet tips for Indian women, Benefits of folate in preconception..."
          data-testid="ai-content-topic"
          className="w-full px-4 py-3 rounded-xl border border-emerald_deep/10 bg-white text-emerald_deep placeholder:text-text_muted/40 focus:outline-none focus:ring-2 focus:ring-accent/30 text-sm" />
      </div>

      {/* Tone & Context Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-text_muted mb-2 tracking-wider uppercase">Tone</label>
          <div className="flex flex-wrap gap-2">
            {TONES.map((t) => (
              <button key={t.id} onClick={() => setTone(t.id)}
                data-testid={`tone-${t.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  tone === t.id
                    ? "bg-emerald_deep text-bone"
                    : "bg-white text-text_muted border border-emerald_deep/10 hover:border-emerald_deep/30"
                }`}>{t.label}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs text-text_muted mb-2 tracking-wider uppercase">Additional Context (optional)</label>
          <input type="text" value={additionalContext} onChange={(e) => setAdditionalContext(e.target.value)}
            placeholder="Any specific details, keywords, or instructions..."
            data-testid="ai-content-context"
            className="w-full px-4 py-3 rounded-xl border border-emerald_deep/10 bg-white text-emerald_deep placeholder:text-text_muted/40 focus:outline-none focus:ring-2 focus:ring-accent/30 text-sm" />
        </div>
      </div>

      {/* Generate Button */}
      <button onClick={handleGenerate} disabled={generating || !topic.trim()}
        data-testid="ai-generate-btn"
        className="flex items-center gap-2 bg-emerald_deep text-bone px-6 py-3 rounded-xl font-medium hover:bg-emerald_deep/90 transition-all disabled:opacity-50 text-sm">
        {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
        {generating ? "Generating..." : "Generate Content"}
      </button>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-xl" data-testid="ai-content-error">
          {error}
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="bg-white border border-emerald_deep/10 rounded-2xl overflow-hidden" data-testid="ai-content-result">
          <div className="flex items-center justify-between px-5 py-3 border-b border-emerald_deep/5 bg-bone/50">
            <span className="text-xs text-text_muted font-medium tracking-wider uppercase">Generated Content</span>
            <button onClick={handleCopy} data-testid="copy-content-btn"
              className="flex items-center gap-1.5 text-xs text-emerald_deep hover:text-accent transition-colors">
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <div className="p-5 text-sm text-text_main leading-relaxed whitespace-pre-wrap max-h-[500px] overflow-y-auto" data-testid="ai-content-output">
            {result}
          </div>
        </div>
      )}
    </div>
  );
}
